import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import SessionTimeoutWarning from './SessionTimeoutWarning';
import { getResolvedPortalTitle } from '../utils/portalBranding';
import { usePermissions } from '../hooks/usePermissions';
import { useModules } from '../contexts/ModulesContext';
import {
  approvalRequestsAPI,
  adminAPI,
  messagingPlatformAPI,
  notificationsAPI,
  dashboardAPI,
  peoplePlatformAPI,
} from '@/services';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from './LanguageSwitcher';
import {
  isBoardOfficerRole,
  isMemberEffectiveRole,
  isOrgOrSaasAdminRole,
  isSaasAdminRole,
} from '../utils/iamRoles';
import {
  isPortalAdminPath,
  toPortalAdminNavHref,
  isPortalAdminNavActive,
  PORTAL_LOGIN_PATH,
} from '../utils/portalAdminNav';
import { loadMergedNotifications } from '../utils/mergePlatformNotifications';
import {
  MAIN_NAV_DEFS,
  SAAS_ADMIN_NAV_DEFS,
  ADMIN_NAV_DEFS,
  MEMBER_BOTTOM_TAB_DEFS,
} from '../navigation/dashboardNavConfig';
import { memberGreetingI18nKey, resolveGreetingMemberName } from '../utils/memberGreeting';

const DashboardLayout = () => {
  // Sidebar ouvert par défaut sur desktop, fermé sur mobile
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Vérifier si on est sur desktop au montage
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024; // lg breakpoint
    }
    return true; // Par défaut ouvert (desktop)
  });
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [approvalRequestsCount, setApprovalRequestsCount] = useState(0);
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [unreadAnnouncementsCount, setUnreadAnnouncementsCount] = useState(0);
  /** Profil People / stats perso — le JWT n’a souvent pas le vrai nom. */
  const [memberGreetingProfile, setMemberGreetingProfile] = useState(null);
  const [memberProfileSheetOpen, setMemberProfileSheetOpen] = useState(false);
  const [supportMenuOpen, setSupportMenuOpen] = useState(false);
  /** Sous-menus navigation membre (style UNFCU) : 'payments' | 'services' | 'profile' */
  const [memberNavDropdown, setMemberNavDropdown] = useState(null);
  const [memberMoreSheetOpen, setMemberMoreSheetOpen] = useState(false);
  const memberNavRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, logout } = useAuth();
  const { settings } = useSettings();
  const organizationBrandName = getResolvedPortalTitle(settings);
  const { hasPermission, canAccessModule, role } = usePermissions();
  const { isRouteEnabled, loading: modulesLoading } = useModules();
  const isAdmin = hasPermission('dashboard.stats_advanced') || hasPermission('dashboard.stats');
  const canViewAuditInsights = hasPermission('dashboard.stats_advanced') || hasPermission('dashboard.stats');
  const isMemberRole = isMemberEffectiveRole(role);
  const hasMemberProfileLink = !!(user?.member_id || user?.person_id);
  /** Sous `/portal/*`, toujours le shell admin (fenêtre dédiée). Sinon officers avec fiche → shell membre. */
  const isMemberPortal =
    !isPortalAdminPath(location.pathname) &&
    (isMemberRole || (isBoardOfficerRole(role) && hasMemberProfileLink));

  const handleLogout = () => {
    const loginPath = isPortalAdminPath(location.pathname) ? PORTAL_LOGIN_PATH : '/login';
    logout();
    navigate(loginPath);
  };

  const { showWarning, countdown, dismissWarning } = useSessionTimeout({
    isAuthenticated,
    token,
    timeoutMinutes: user?.session_timeout_minutes,
    onLogout: handleLogout,
  });

  // Références pour détecter les clics en dehors du menu
  const profileMenuDesktopRef = useRef(null);
  const profileMenuMobileRef = useRef(null);
  const supportMenuRef = useRef(null);

  // Fermer le menu si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideDesktop = profileMenuDesktopRef.current && !profileMenuDesktopRef.current.contains(event.target);
      const isOutsideMobile = profileMenuMobileRef.current && !profileMenuMobileRef.current.contains(event.target);
      
      if ((profileMenuDesktopRef.current || profileMenuMobileRef.current) && isOutsideDesktop && isOutsideMobile) {
        setProfileMenuOpen(false);
      }
    };

    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileMenuOpen]);

  // Charger le nombre de demandes d'approbation en attente
  const canApprove = hasPermission('contributions.approve');
  
  useEffect(() => {
    const loadApprovalRequestsCount = async () => {
      if (canApprove) {
        try {
          const response = await approvalRequestsAPI.getCount();
          const count = response.data?.count || 0;
          setApprovalRequestsCount(count);
          if (count > 0) {
            console.log(`[DashboardLayout] ${count} notification(s) non lue(s)`);
          }
        } catch (error) {
          console.error('Erreur lors du chargement du nombre de demandes:', error);
          console.error('Détails de l\'erreur:', error.response?.data || error.message);
          // En cas d'erreur, réinitialiser le compteur
          setApprovalRequestsCount(0);
        }
      } else {
        // Si l'utilisateur n'a pas la permission, réinitialiser le compteur
        setApprovalRequestsCount(0);
      }
    };

    loadApprovalRequestsCount();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(loadApprovalRequestsCount, 30000);
    // Rafraîchir aussi quand on change de page
    const handleLocationChange = () => loadApprovalRequestsCount();
    window.addEventListener('focus', handleLocationChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleLocationChange);
    };
  }, [canApprove, location.pathname]);

  // Charger le nombre d'utilisateurs actifs (admin seulement)
  useEffect(() => {
    const loadActiveUsersCount = async () => {
      if (isAdmin) {
        try {
          const [activeUsersRes] = await Promise.allSettled([adminAPI.getActiveUsers()]);
          const users =
            activeUsersRes.status === 'fulfilled'
              ? (activeUsersRes.value?.data?.users || [])
              : [];
          // "Utilisateurs en ligne" = utilisateurs avec session active.
          const activeSessionsCount = users.filter((u) => u?.is_active_session === true).length;
          setActiveUsersCount(activeSessionsCount);
        } catch (error) {
          console.error('Erreur lors du chargement du nombre d\'utilisateurs actifs:', error);
          setActiveUsersCount(0);
        }
      } else {
        setActiveUsersCount(0);
      }
    };

    loadActiveUsersCount();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(loadActiveUsersCount, 30000);
    // Rafraîchir aussi quand on change de page
    const handleLocationChange = () => loadActiveUsersCount();
    window.addEventListener('focus', handleLocationChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleLocationChange);
    };
  }, [isAdmin, location.pathname]);

  // Charger le nombre d'annonces non lues (vue membre)
  useEffect(() => {
    const loadUnreadAnnouncementsCount = async () => {
      if (!isMemberPortal) {
        setUnreadAnnouncementsCount(0);
        return;
      }
      try {
        const merged = await loadMergedNotifications(notificationsAPI, messagingPlatformAPI);
        const unreadCount = merged.filter((n) => !n?.read).length;
        setUnreadAnnouncementsCount(unreadCount);
      } catch (_error) {
        setUnreadAnnouncementsCount(0);
      }
    };

    loadUnreadAnnouncementsCount();
    const interval = setInterval(loadUnreadAnnouncementsCount, 30000);
    const handleFocus = () => loadUnreadAnnouncementsCount();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isMemberPortal, location.pathname]);

  // Nom d’affichage pour « Bonjour / Bonsoir {prénom} » (fiche People + stats perso)
  useEffect(() => {
    if (!isMemberPortal) {
      setMemberGreetingProfile(null);
      return;
    }
    let cancelled = false;
    const profileId = String(user?.member_id || user?.person_id || '').trim();

    const loadGreetingName = async () => {
      const sources = [];
      let personal = null;
      try {
        const personalRes = await dashboardAPI.getPersonalStats().catch(() => null);
        personal = personalRes?.data && typeof personalRes.data === 'object' ? personalRes.data : null;
      } catch {
        /* optionnel */
      }
      const idFromStats = String(
        personal?.person_id || personal?.member_id || profileId || ''
      ).trim();
      // People d’abord : le JWT / stats perso ont souvent name = « Membre »
      if (idFromStats) {
        try {
          const peopleRes = await peoplePlatformAPI.getById(idFromStats);
          const people = peopleRes?.data;
          if (people && typeof people === 'object') sources.push(people);
        } catch {
          /* profil People optionnel */
        }
      }
      if (personal) sources.push(personal);
      if (!cancelled) {
        setMemberGreetingProfile(sources.length ? sources : null);
      }
    };

    loadGreetingName();
    return () => {
      cancelled = true;
    };
  }, [isMemberPortal, user?.id, user?.person_id, user?.member_id]);

  useEffect(() => {
    setSupportMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!supportMenuOpen) return;

    const handleOutside = (event) => {
      if (supportMenuRef.current && !supportMenuRef.current.contains(event.target)) {
        setSupportMenuOpen(false);
      }
    };

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setSupportMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [supportMenuOpen]);

  const supportMenuHiddenRoutes = [
    '/dashboard',
    '/contributions',
    '/transactionDetails',
    '/my-fund',
    '/documents',
    '/notifications',
    '/meetings',
  ];
  const hideSupportBubble = supportMenuHiddenRoutes.some((route) =>
    location.pathname === route || location.pathname.startsWith(`${route}/`)
  );

  const path = location.pathname;
  /** Profil unifié (/profile) ; sous-pages vers fiche People et IAM. */
  const linkedPeopleProfileId = user?.member_id || user?.person_id || '';
  const monCompteHref = user?.id ? '/profile' : '/dashboard';
  const iamSecurityHref = user?.id ? '/profile/compte' : null;

  const memberPathPayments =
    path === '/contributions' ||
    path === '/assistance' ||
    path === '/transactionDetails/cotisations' ||
    path === '/transactionDetails/allocations' ||
    path === '/my-fund/financial' ||
    path.startsWith('/contributions/') ||
    path.startsWith('/assistance/') ||
    path.startsWith('/transactionDetails/cotisations/') ||
    path.startsWith('/transactionDetails/allocations/') ||
    path.startsWith('/my-fund/financial/');
  const memberPathServices =
    path === '/documents' ||
    path === '/calendar' ||
    path === '/support' ||
    path === '/meetings' ||
    path.startsWith('/documents/') ||
    path.startsWith('/calendar/') ||
    path.startsWith('/meetings/');
  const memberPathMyFundIndex = path === '/my-fund' || path === '/my-fund/';
  const memberPathProfile =
    path.startsWith('/members/') ||
    path.startsWith('/users/') ||
    path.startsWith('/profile') ||
    path === '/notifications' ||
    memberPathMyFundIndex;
  const memberPathMoreSheet =
    memberPathServices ||
    memberPathProfile ||
    path === '/my-fund/financial' ||
    path.startsWith('/my-fund/financial/');

  useEffect(() => {
    setMemberNavDropdown(null);
  }, [path]);

  useEffect(() => {
    if (!memberNavDropdown) return undefined;
    const onDoc = (e) => {
      if (memberNavRef.current && !memberNavRef.current.contains(e.target)) {
        setMemberNavDropdown(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [memberNavDropdown]);

  const { t, i18n } = useTranslation('common');
  const genericMemberLabel = t('memberPortal.member');
  const greetingMemberName = useMemo(
    () =>
      resolveGreetingMemberName(
        [
          ...(Array.isArray(memberGreetingProfile) ? memberGreetingProfile : []),
          user,
        ],
        genericMemberLabel
      ),
    [memberGreetingProfile, user, genericMemberLabel]
  );

  const navigation = useMemo(
    () =>
      MAIN_NAV_DEFS.filter((item) => {
        if (item.id === 'members' && isMemberRole) return false;
        let hasRequiredPermission = false;
        if (Array.isArray(item.permission)) {
          hasRequiredPermission = item.permission.some((perm) => hasPermission(perm));
        } else {
          hasRequiredPermission = hasPermission(item.permission);
        }
        if (!hasRequiredPermission) return false;
        if (item.route && !modulesLoading) return isRouteEnabled(item.route);
        return true;
      }),
    [isMemberRole, hasPermission, modulesLoading, isRouteEnabled],
  );

  const allAdminNavigationItems = useMemo(() => {
    const saas = isSaasAdminRole(role) ? SAAS_ADMIN_NAV_DEFS : [];
    return [...saas, ...ADMIN_NAV_DEFS];
  }, [role]);

  const adminNavigation = useMemo(
    () =>
      allAdminNavigationItems.filter((item) => {
        if (item.saasOnly && !isSaasAdminRole(role)) {
          return false;
        }
        if (!item.permission) return true;
        let hasRequiredPermission = false;
        if (Array.isArray(item.permission)) {
          hasRequiredPermission = item.permission.some((perm) => hasPermission(perm));
        } else {
          hasRequiredPermission = hasPermission(item.permission);
        }
        if (!hasRequiredPermission) return false;
        if (
          item.route &&
          !modulesLoading &&
          item.route !== '/modules' &&
          item.route !== '/settings' &&
          item.route !== '/roles-permissions'
        ) return isRouteEnabled(item.route);
        return true;
      }),
    [allAdminNavigationItems, role, hasPermission, modulesLoading, isRouteEnabled],
  );

  const adminNavSections = useMemo(
    () =>
      [
        {
          key: 'identity-access',
          titleKey: 'adminSection.identity',
          items: adminNavigation.filter((item) => ['/users', '/roles', '/commissions'].includes(item.href)),
        },
        {
          key: 'configuration',
          titleKey: 'adminSection.configuration',
          items: adminNavigation.filter((item) => ['/settings', '/modules'].includes(item.href)),
        },
        {
          key: 'pilotage',
          titleKey: 'adminSection.pilotage',
          items: adminNavigation.filter((item) =>
            ['/admin', '/admin/ledgera', '/saas', '/saas/organizations'].includes(item.href),
          ),
        },
      ].filter((section) => section.items.length > 0),
    [adminNavigation],
  );
  const canShowMemberPortalAdminLink = false;
  const memberPortalAdminHref = adminNavigation[0]?.href || '/admin';

  const memberBottomTabs = useMemo(
    () =>
      MEMBER_BOTTOM_TAB_DEFS.map((tab) => ({
        ...tab,
        short: t(tab.shortKey),
      })),
    [t, i18n.language],
  );

  const memberMoreSheetLinks = useMemo(
    () =>
      [
        { to: '/documents', labelKey: 'nav.documents' },
        { to: '/calendar', labelKey: 'nav.calendar' },
        { to: '/support', labelKey: 'memberPortal.supportMember' },
        { to: '/notifications', labelKey: 'memberPortal.announcements' },
        { to: '/my-fund', labelKey: 'memberPortal.myOrgFund' },
        { to: '/my-fund/financial', labelKey: 'memberPortal.ledgeraMovements' },
        { to: monCompteHref, labelKey: 'memberPortal.myAccount' },
      ].filter(Boolean),
    [monCompteHref],
  );

  const headerPageTitle = useMemo(() => {
    const path = location.pathname;
    const match =
      navigation.find((item) =>
        isPortalAdminNavActive(path, item.href, toPortalAdminNavHref(path, item.href)),
      ) ||
      adminNavigation.find((item) =>
        isPortalAdminNavActive(path, item.href, toPortalAdminNavHref(path, item.href)),
      );
    return match ? t(match.nameKey) : t('nav.dashboard');
  }, [navigation, adminNavigation, location.pathname, t]);

  const localeTag = i18n.language?.startsWith('en') ? 'en-US' : 'fr-FR';

  const memberLinkUnderline = (active) =>
    [
      'relative inline-flex items-center gap-1 border-b-2 py-2.5 px-2.5 text-[15px] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] rounded-t',
      active
        ? 'border-[#2f6fd4] font-semibold text-[#0f3350]'
        : 'border-transparent text-[#4a5a6d] hover:text-[#0f3350] hover:border-slate-200',
    ].join(' ');

  if (isMemberPortal) {
    return (
      <>
      {showWarning && (
        <SessionTimeoutWarning
          countdown={countdown}
          onStayConnected={dismissWarning}
          onLogout={handleLogout}
        />
      )}
      <div className="min-h-screen member-mobile-atmos flex flex-col">
        <header className="sticky top-0 z-30 border-b border-white/10 text-white pt-[env(safe-area-inset-top,0px)] mm-header-mesh md:border-[#0e314c]">
          <div className="max-w-7xl mx-auto px-3 sm:px-6">
            <div className="md:hidden flex min-h-[48px] items-center justify-between gap-2 py-2">
              <div className="min-w-0 flex-1 pr-2">
                <p className="font-display text-[14px] font-extrabold tracking-tight leading-none text-white">
                  {organizationBrandName}
                </p>
                {path !== '/dashboard' && (
                  <p className="mt-0.5 truncate text-[11px] font-medium text-slate-100">
                    {greetingMemberName}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <LanguageSwitcher variant="dark" />
                <Link
                  to="/notifications"
                  className="relative inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/12 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 active:scale-[0.97]"
                  aria-label={t('memberPortal.announcementsAria')}
                  title={t('memberPortal.announcements')}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <span
                    className={`absolute -right-0.5 -top-0.5 inline-flex min-h-[16px] min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold leading-none text-white ${
                      unreadAnnouncementsCount > 0 ? 'bg-red-500' : 'bg-slate-500/80'
                    }`}
                  >
                    {unreadAnnouncementsCount > 99 ? '99+' : unreadAnnouncementsCount}
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => setMemberProfileSheetOpen(true)}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#ecf2f8] text-[#113a5b] shadow-sm ring-1 ring-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 active:scale-[0.97]"
                  title={t('memberPortal.accountMenuAria')}
                  aria-label={t('memberPortal.accountMenuAria')}
                >
                  {path === '/dashboard' ? (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  ) : (
                    <span className="text-sm font-semibold">{greetingMemberName?.charAt(0) || 'U'}</span>
                  )}
                </button>
              </div>
            </div>

            <div className="hidden md:flex py-2.5 items-center justify-between gap-2 text-[14px] text-white">
              <div className="font-semibold truncate text-white">
                {t(memberGreetingI18nKey(), {
                  name: greetingMemberName,
                })}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-slate-100 md:gap-4">
                <LanguageSwitcher variant="dark" />
                <Link
                  to="/documents"
                  className="inline-flex items-center px-2 py-1 rounded-md hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                >
                  {t('memberPortal.myDocuments')}
                </Link>
                <Link
                  to="/notifications"
                  className="relative inline-flex items-center px-2 py-1 rounded-md hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                >
                  {t('memberPortal.announcements')}
                  <span
                    className={`absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-none text-white rounded-full ${
                      unreadAnnouncementsCount > 0 ? 'bg-red-500 animate-pulse' : 'bg-slate-400'
                    }`}
                    title={
                      unreadAnnouncementsCount > 0
                        ? t('memberPortal.announcementsUnreadTitle', { count: unreadAnnouncementsCount })
                        : t('memberPortal.announcementsNoneTitle')
                    }
                  >
                    {unreadAnnouncementsCount > 99 ? '99+' : unreadAnnouncementsCount}
                  </span>
                </Link>
                {canShowMemberPortalAdminLink && (
                  <Link
                    to={memberPortalAdminHref}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                    title={t('memberPortal.adminLink', { defaultValue: 'Admin' })}
                    aria-label={t('memberPortal.adminLink', { defaultValue: 'Admin' })}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11.983 5.346a1 1 0 011.034 0l1.255.76a1 1 0 001.01.03l1.3-.708a1 1 0 011.37.42l.69 1.308a1 1 0 00.79.54l1.49.164a1 1 0 01.88 1.06l-.118 1.475a1 1 0 00.42.92l1.173.922a1 1 0 01.26 1.41l-.847 1.206a1 1 0 00-.12 1.008l.57 1.376a1 1 0 01-.62 1.29l-1.407.49a1 1 0 00-.67.768l-.238 1.48a1 1 0 01-1.18.83l-1.45-.296a1 1 0 00-.96.29l-1.05 1.04a1 1 0 01-1.43 0l-1.05-1.04a1 1 0 00-.96-.29l-1.45.296a1 1 0 01-1.18-.83l-.238-1.48a1 1 0 00-.67-.768l-1.407-.49a1 1 0 01-.62-1.29l.57-1.376a1 1 0 00-.12-1.008l-.847-1.206a1 1 0 01.26-1.41l1.173-.922a1 1 0 00.42-.92l-.118-1.475a1 1 0 01.88-1.06l1.49-.164a1 1 0 00.79-.54l.69-1.308a1 1 0 011.37-.42l1.3.708a1 1 0 001.01-.03l1.255-.76z"
                      />
                      <circle cx="12" cy="12" r="2.75" />
                    </svg>
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center px-2 py-1 rounded-md hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                >
                  {t('memberPortal.logout')}
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="hidden md:block sticky top-[52px] z-20 border-b border-[#d7dee7] bg-white/95 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6" ref={memberNavRef}>
            <div className="flex h-12 items-center justify-between gap-2">
              <Link
                to="/dashboard"
                className="shrink-0 font-extrabold text-[#0f3350] tracking-tight text-[18px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] rounded px-1"
              >
                {organizationBrandName}
              </Link>
              <nav className="hidden min-w-0 flex-1 md:flex items-center justify-center gap-0.5 lg:gap-1 text-[15px]" aria-label="Navigation principale">
                <Link
                  to="/dashboard"
                  className={memberLinkUnderline(path === '/dashboard')}
                >
                  {t('memberPortal.mySpace')}
                </Link>
                <div className="relative">
                  <button
                    type="button"
                    aria-expanded={memberNavDropdown === 'payments'}
                    aria-haspopup="menu"
                    onClick={() => setMemberNavDropdown((d) => (d === 'payments' ? null : 'payments'))}
                    className={memberLinkUnderline(
                      memberPathPayments || memberNavDropdown === 'payments'
                    )}
                  >
                    {t('memberPortal.contributionsPayments')}
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {memberNavDropdown === 'payments' && (
                    <div
                      className="absolute left-0 top-full z-40 mt-0.5 min-w-[15rem] rounded-lg border border-slate-200 bg-white py-1.5 text-sm shadow-lg"
                      role="menu"
                    >
                      <Link
                        to="/contributions"
                        className="flex min-h-[40px] items-center justify-between gap-2 px-4 py-2 text-left text-slate-800 hover:bg-slate-50"
                        onClick={() => setMemberNavDropdown(null)}
                        role="menuitem"
                      >
                        {t('memberPortal.contribution')}
                        <span className="text-slate-400" aria-hidden>
                          ›
                        </span>
                      </Link>
                      <Link
                        to="/assistance"
                        className="flex min-h-[40px] items-center justify-between gap-2 px-4 py-2 text-left text-slate-800 hover:bg-slate-50"
                        onClick={() => setMemberNavDropdown(null)}
                        role="menuitem"
                      >
                        {t('memberPortal.allocation')}
                        <span className="text-slate-400" aria-hidden>
                          ›
                        </span>
                      </Link>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    aria-expanded={memberNavDropdown === 'services'}
                    aria-haspopup="menu"
                    onClick={() => setMemberNavDropdown((d) => (d === 'services' ? null : 'services'))}
                    className={memberLinkUnderline(
                      memberPathServices || memberNavDropdown === 'services'
                    )}
                  >
                    {t('memberPortal.services')}
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {memberNavDropdown === 'services' && (
                    <div
                      className="absolute right-0 top-full z-40 mt-0.5 min-w-[15rem] rounded-lg border border-slate-200 bg-white py-1.5 text-sm shadow-lg"
                      role="menu"
                    >
                      <Link
                        to="/documents"
                        className="flex min-h-[40px] items-center justify-between gap-2 px-4 py-2 text-slate-800 hover:bg-slate-50"
                        onClick={() => setMemberNavDropdown(null)}
                        role="menuitem"
                      >
                        {t('nav.documents')}
                        <span className="text-slate-400" aria-hidden>
                          ›
                        </span>
                      </Link>
                      <Link
                        to="/calendar"
                        className="flex min-h-[40px] items-center justify-between gap-2 px-4 py-2 text-slate-800 hover:bg-slate-50"
                        onClick={() => setMemberNavDropdown(null)}
                        role="menuitem"
                      >
                        {t('nav.calendar')}
                        <span className="text-slate-400" aria-hidden>
                          ›
                        </span>
                      </Link>
                      <Link
                        to="/support"
                        className="flex min-h-[40px] items-center justify-between gap-2 px-4 py-2 text-slate-800 hover:bg-slate-50"
                        onClick={() => setMemberNavDropdown(null)}
                        role="menuitem"
                      >
                        {t('memberPortal.supportMember')}
                        <span className="text-slate-400" aria-hidden>
                          ›
                        </span>
                      </Link>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    aria-expanded={memberNavDropdown === 'profile'}
                    aria-haspopup="menu"
                    onClick={() => setMemberNavDropdown((d) => (d === 'profile' ? null : 'profile'))}
                    className={memberLinkUnderline(
                      memberPathProfile || memberNavDropdown === 'profile'
                    )}
                  >
                    {t('memberPortal.profile')}
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {memberNavDropdown === 'profile' && (
                    <div
                      className="absolute right-0 top-full z-40 mt-0.5 min-w-[15rem] rounded-lg border border-slate-200 bg-white py-1.5 text-sm shadow-lg"
                      role="menu"
                    >
                      <Link
                        to={monCompteHref}
                        className="flex min-h-[40px] items-center justify-between gap-2 px-4 py-2 text-slate-800 hover:bg-slate-50"
                        onClick={() => setMemberNavDropdown(null)}
                        role="menuitem"
                      >
                        {t('memberPortal.myAccount')}
                        <span className="text-slate-400" aria-hidden>
                          ›
                        </span>
                      </Link>
                      <Link
                        to="/notifications"
                        className="flex min-h-[40px] items-center justify-between gap-2 px-4 py-2 text-slate-800 hover:bg-slate-50"
                        onClick={() => setMemberNavDropdown(null)}
                        role="menuitem"
                      >
                        {t('memberPortal.announcements')}
                        {unreadAnnouncementsCount > 0 ? (
                          <span className="rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                            {unreadAnnouncementsCount > 99 ? '99+' : unreadAnnouncementsCount}
                          </span>
                        ) : (
                          <span className="text-slate-400" aria-hidden>
                            ›
                          </span>
                        )}
                      </Link>
                      <Link
                        to="/my-fund"
                        className="flex min-h-[40px] items-center justify-between gap-2 px-4 py-2 text-left text-slate-800 hover:bg-slate-50"
                        onClick={() => setMemberNavDropdown(null)}
                        role="menuitem"
                      >
                        {t('memberPortal.myOrgFund')}
                        <span className="text-slate-400" aria-hidden>
                          ›
                        </span>
                      </Link>
                      <Link
                        to="/my-fund/financial"
                        className="flex min-h-[40px] items-center justify-between gap-2 px-4 py-2 text-left text-slate-800 hover:bg-slate-50"
                        onClick={() => setMemberNavDropdown(null)}
                        role="menuitem"
                      >
                        {t('memberPortal.ledgeraMovements')}
                        <span className="text-slate-400" aria-hidden>
                          ›
                        </span>
                      </Link>
                    </div>
                  )}
                </div>
              </nav>
              <Link
                to={monCompteHref}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#d8e2ee] bg-[#ecf2f8] text-base font-semibold text-[#113a5b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
                title={t('memberPortal.myAccount')}
              >
                {user?.name?.charAt(0) || 'U'}
              </Link>
            </div>
            {path === '/dashboard' && (
              <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 py-2.5">
                <span className="pr-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  {t('memberPortal.quickAccess')}
                </span>
                <Link
                  to="/assistance"
                  className="inline-flex min-h-[34px] items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 text-xs font-semibold text-[#0f3350] shadow-sm hover:border-[#bcd4f6] hover:bg-[#f8fbff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
                >
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#113a5b] text-[10px] font-bold text-white">
                    +
                  </span>
                  {t('memberPortal.newAllocation')}
                </Link>
                <Link
                  to="/contributions"
                  className="inline-flex min-h-[34px] items-center rounded-full border border-slate-200 bg-white px-3.5 text-xs font-semibold text-[#0f3350] hover:border-[#bcd4f6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
                >
                  {t('memberPortal.myContributions')}
                </Link>
                <Link
                  to="/my-fund"
                  className="inline-flex min-h-[34px] items-center rounded-full border border-slate-200 bg-white px-3.5 text-xs font-semibold text-[#0f3350] hover:border-[#bcd4f6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
                >
                  {t('memberPortal.myOrganization')}
                </Link>
                <Link
                  to="/documents"
                  className="inline-flex min-h-[34px] items-center rounded-full border border-slate-200 bg-white px-3.5 text-xs font-semibold text-[#0f3350] hover:border-[#bcd4f6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
                >
                  {t('nav.documents')}
                </Link>
              </div>
            )}
          </div>
        </div>

        <main className="flex-1 w-full">
          <div
            className="max-w-7xl mx-auto px-3 py-4 pb-[calc(5.75rem+env(safe-area-inset-bottom,0px))] sm:px-6 sm:py-5 md:pb-5"
          >
            <Outlet />
          </div>
        </main>

        {!hideSupportBubble && (
          <div ref={supportMenuRef} className="md:hidden fixed right-4 z-40 bottom-[calc(env(safe-area-inset-bottom)+5rem)]">
          {supportMenuOpen && (
            <div className="mb-2 w-40 rounded-2xl border border-slate-200 bg-white shadow-xl p-1.5 animate-in slide-in-from-bottom">
              <Link
                to="/support"
                onClick={() => setSupportMenuOpen(false)}
                className="flex min-h-[40px] items-center rounded-xl px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {t('memberPortal.support')}
              </Link>
              <Link
                to="/documents"
                onClick={() => setSupportMenuOpen(false)}
                className="flex min-h-[40px] items-center rounded-xl px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {t('nav.documents')}
              </Link>
            </div>
          )}
          <button
            type="button"
            onClick={() => setSupportMenuOpen((prev) => !prev)}
            className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#113a5b] text-white shadow-xl border border-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
            aria-label={t('memberPortal.openSupportMenu')}
            title={t('memberPortal.support')}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4v-4z" />
            </svg>
          </button>
          </div>
        )}

        {memberProfileSheetOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            <button
              type="button"
              className="absolute inset-0 bg-black/40 backdrop-blur-[1px] animate-fade-in"
              aria-label={t('memberPortal.closeProfileMenu')}
              onClick={() => setMemberProfileSheetOpen(false)}
            />
            <div className="absolute inset-x-0 bottom-0 rounded-t-3xl bg-white shadow-2xl border-t border-slate-200 p-4 pb-[calc(env(safe-area-inset-bottom)+14px)] animate-in slide-in-from-bottom">
              <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-slate-300" />
              <div className="mb-3">
                <p className="text-xs uppercase tracking-wide text-slate-600">{t('memberPortal.memberProfile')}</p>
                <p className="text-base font-semibold text-slate-900 truncate">
                  {greetingMemberName}
                </p>
              </div>
              <div className="space-y-2">
                <Link
                  to={monCompteHref}
                  onClick={() => setMemberProfileSheetOpen(false)}
                  className="flex min-h-[44px] items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-800"
                >
                  <span>{t('memberPortal.myAccount')}</span>
                  <span className="text-slate-500" aria-hidden>
                    ›
                  </span>
                </Link>
                <Link
                  to="/documents"
                  onClick={() => setMemberProfileSheetOpen(false)}
                  className="flex min-h-[44px] items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-800"
                >
                  <span>{t('memberPortal.myDocuments')}</span>
                  <span className="text-slate-500" aria-hidden>
                    ›
                  </span>
                </Link>
                <Link
                  to="/my-fund"
                  onClick={() => setMemberProfileSheetOpen(false)}
                  className="flex min-h-[44px] items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-800"
                >
                  <span>{t('memberPortal.myOrgFund')}</span>
                  <span className="text-slate-500" aria-hidden>
                    ›
                  </span>
                </Link>
                <Link
                  to="/my-fund/financial"
                  onClick={() => setMemberProfileSheetOpen(false)}
                  className="flex min-h-[44px] items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-800"
                >
                  <span>{t('memberPortal.ledgeraMovements')}</span>
                  <span className="text-slate-500" aria-hidden>
                    ›
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setMemberProfileSheetOpen(false);
                    handleLogout();
                  }}
                  className="w-full min-h-[44px] rounded-xl bg-red-50 text-red-700 border border-red-200 px-3 py-2.5 text-sm font-semibold"
                >
                  {t('memberPortal.logout')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Barre d’onglets membre : dock verre + état actif fort */}
        <nav
          className="mm-tabbar pointer-events-none fixed inset-x-0 bottom-0 z-40 md:hidden"
          aria-label={t('memberPortal.memberNavAria')}
        >
          <div className="pointer-events-auto mm-tabbar__dock mx-auto max-w-md">
            {memberBottomTabs.map((tab) => {
              const paths = tab.matchPaths || [tab.href];
              const isActive = paths.some((p) => path === p || path.startsWith(`${p}/`));
              return (
                <Link
                  key={`member-bottom-${tab.href}`}
                  to={tab.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={`mm-tab mm-press ${isActive ? 'mm-tab--on' : ''}`}
                >
                  <span className="mm-tab__icon" aria-hidden>
                    <svg
                      className="h-[1.15rem] w-[1.15rem]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={isActive ? 2.25 : 1.9}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d={tab.icon} />
                    </svg>
                  </span>
                  <span className="mm-tab__label">{tab.short}</span>
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() => setMemberMoreSheetOpen(true)}
              aria-label={t('memberPortal.moreOptions')}
              aria-expanded={memberMoreSheetOpen}
              className={`mm-tab mm-press ${memberPathMoreSheet || memberMoreSheetOpen ? 'mm-tab--on' : ''}`}
            >
              <span className="mm-tab__icon" aria-hidden>
                <svg
                  className="h-[1.15rem] w-[1.15rem]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={memberPathMoreSheet || memberMoreSheetOpen ? 2.25 : 1.9}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 6h16M4 12h16M4 18h10" />
                </svg>
              </span>
              <span className="mm-tab__label">{t('memberPortal.more')}</span>
            </button>
          </div>
        </nav>

        {memberMoreSheetOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <button
              type="button"
              className="mm-modal-backdrop absolute inset-0"
              aria-label={t('memberPortal.closeMenu')}
              onClick={() => setMemberMoreSheetOpen(false)}
            />
            <div className="mm-modal-panel absolute inset-x-0 bottom-0 max-h-[min(70vh,420px)] overflow-hidden p-0">
              <div className="flex shrink-0 justify-center pt-2" aria-hidden>
                <span className="mm-modal-handle" />
              </div>
              <div className="mm-modal-header">
                <div className="min-w-0">
                  <p className="mm-hero__label text-[#1f6ab8]">{t('memberPortal.more')}</p>
                  <p className="mt-0.5 font-display text-base font-extrabold text-[#0f3350]">
                    {t('memberPortal.servicesSheetTitle')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setMemberMoreSheetOpen(false)}
                  className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
                  aria-label={t('memberPortal.closeMenu')}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-1.5 overflow-y-auto px-3 pb-[max(1rem,env(safe-area-inset-bottom,0px))] pt-1">
                {memberMoreSheetLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMemberMoreSheetOpen(false)}
                    className="mm-press flex min-h-[48px] items-center justify-between rounded-2xl border border-slate-200/80 bg-gradient-to-r from-white to-[#f7fbff] px-3.5 text-sm font-semibold text-[#0f3350]"
                  >
                    {t(item.labelKey)}
                    <span className="text-slate-400" aria-hidden>
                      ›
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      </>
    );
  }

  return (
    <>
    {showWarning && (
      <SessionTimeoutWarning
        countdown={countdown}
        onStayConnected={dismissWarning}
        onLogout={handleLogout}
      />
    )}
    <div className="h-screen flex overflow-hidden bg-[#f5f7fa]">
      {/* Sidebar — style sobre (M365 / Stripe) : fond plat, accent barre bleue */}
      <div
        className={`hidden lg:flex lg:static inset-y-0 left-0 z-50 lg:z-auto ${
          sidebarOpen ? 'w-64' : 'lg:w-20'
        } flex flex-col border-r border-white/[0.06] bg-[#0f172a]`}
      >
        <div className="flex h-14 items-center justify-between border-b border-white/[0.06] px-3">
          <h1
            className={`${
              sidebarOpen
                ? 'truncate px-1 text-[15px] font-semibold tracking-tight text-white'
                : 'hidden'
            }`}
          >
            {organizationBrandName}
          </h1>
          <button
            type="button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden rounded-md p-2 text-slate-400 transition hover:bg-white/[0.06] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]/60 lg:block"
            aria-label={sidebarOpen ? t('layout.sidebarShrink') : t('layout.sidebarExpand')}
            title={sidebarOpen ? t('layout.sidebarShrinkTitle') : t('layout.sidebarExpandTitle')}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
        <nav
          className="mt-3 flex-1 space-y-0.5 overflow-x-hidden overflow-y-auto px-2 pb-4 scrollbar-thin"
          style={{ maxHeight: 'calc(100vh - 200px)' }}
        >
          <div className="space-y-0.5">
            {navigation.map((item) => {
              const navTo = toPortalAdminNavHref(location.pathname, item.href);
              const isActive = isPortalAdminNavActive(location.pathname, item.href, navTo);
              return (
                <Link
                  key={item.id}
                  to={navTo}
                  onClick={() => {
                    if (window.innerWidth < 1024) {
                      setSidebarOpen(false);
                    }
                  }}
                  className={`group relative flex min-h-[40px] items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#2f6fd4]/12 text-white'
                      : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200'
                  }`}
                >
                  {isActive ? (
                    <span
                      aria-hidden
                      className="absolute bottom-1.5 left-0 top-1.5 w-[3px] rounded-full bg-[#2f6fd4]"
                    />
                  ) : null}
                  <svg
                    className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isActive ? 'text-[#7eb0f0]' : 'text-slate-500 group-hover:text-slate-300'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                  </svg>
                  <span className={`${sidebarOpen ? 'truncate' : 'hidden'}`}>{t(item.nameKey)}</span>
                </Link>
              );
            })}
          </div>

          {adminNavigation.length > 0 && (
            <div className="mt-4 space-y-0.5 border-t border-white/[0.06] pt-3">
              <div className={`${sidebarOpen ? 'px-3 pb-1' : 'px-2 py-2'}`}>
                <span
                  className={`text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${
                    sidebarOpen ? '' : 'hidden'
                  }`}
                >
                  {t('layout.administration')}
                </span>
                {sidebarOpen && (
                  <p className="mt-1 px-0 text-[10px] leading-snug text-slate-600">
                    {t('layout.adminBlurb')}
                  </p>
                )}
              </div>
              {adminNavSections.map((section) => (
                <div key={section.key} className="space-y-0.5">
                  {sidebarOpen && (
                    <p className="px-3 pt-2 text-[10px] uppercase tracking-wider text-slate-600">
                      {t(section.titleKey)}
                    </p>
                  )}
                  {section.items.map((item) => {
                    const navTo = toPortalAdminNavHref(location.pathname, item.href);
                    const isActive = isPortalAdminNavActive(location.pathname, item.href, navTo);
                    return (
                      <Link
                        key={item.id}
                        to={navTo}
                        onClick={() => {
                          if (window.innerWidth < 1024) {
                            setSidebarOpen(false);
                          }
                        }}
                        className={`group relative flex min-h-[40px] items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-[#2f6fd4]/12 text-white'
                            : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200'
                        }`}
                      >
                        {isActive ? (
                          <span
                            aria-hidden
                            className="absolute bottom-1.5 left-0 top-1.5 w-[3px] rounded-full bg-[#2f6fd4]"
                          />
                        ) : null}
                        <svg
                          className={`mr-3 h-5 w-5 flex-shrink-0 ${
                            isActive ? 'text-[#7eb0f0]' : 'text-slate-500 group-hover:text-slate-300'
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                        </svg>
                        <span className={`${sidebarOpen ? 'truncate' : 'hidden'}`}>{t(item.nameKey)}</span>
                      </Link>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </nav>

        {sidebarOpen && user && (
          <div className="border-t border-white/[0.06] p-3">
            <div className="mb-3 flex items-center">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#2f6fd4] text-sm font-semibold text-white">
                {user.name?.charAt(0) || 'U'}
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-100">{user.name}</p>
                <p className="truncate text-xs capitalize text-slate-500">{user.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex min-h-[40px] w-full items-center justify-center rounded-md bg-white/[0.04] px-3 py-2 text-sm font-medium text-slate-400 transition hover:bg-white/[0.08] hover:text-slate-200"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {t('layout.logout')}
            </button>
          </div>
        )}
      </div>

      {/* Main content with modern header */}
      <div className="flex-1 flex flex-col overflow-hidden w-full lg:w-auto">
        <header className="bg-white shadow-sm border-b border-gray-200 relative z-30">
          <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                {headerPageTitle}
              </h1>
              <p className="text-sm text-gray-500 mt-1 hidden sm:block">
                {new Date().toLocaleDateString(localeTag, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              <LanguageSwitcher />
              {/* Notification Bell */}
              <NotificationBell />
              
              {/* Badge pour les utilisateurs en ligne (Admin seulement) */}
              {isAdmin && (
                <Link
                  to={toPortalAdminNavHref(location.pathname, '/admin')}
                  onClick={() => {
                    // Si on est déjà sur /admin, on peut ajouter un paramètre ou simplement rester
                  }}
                  className="relative inline-flex items-center justify-center p-2 sm:p-2.5 min-w-[44px] min-h-[44px] text-gray-700 hover:text-green-600 transition-colors rounded-lg hover:bg-gray-100"
                  title={`${activeUsersCount} utilisateur(s) en ligne — Statistiques & audits`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span
                    className={`absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 rounded-full min-w-[20px] shadow-lg ${
                      activeUsersCount > 0 ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                  >
                    {activeUsersCount > 99 ? '99+' : activeUsersCount}
                  </span>
                </Link>
              )}
              
              {/* Notification badge pour les notifications non lues */}
              {canApprove && (
                <Link
                  to="/notifications?tab=approvals"
                  className="relative inline-flex items-center justify-center p-2 sm:p-2.5 min-w-[44px] min-h-[44px] text-gray-700 hover:text-indigo-600 transition-colors rounded-lg hover:bg-gray-100"
                  title={approvalRequestsCount > 0 ? `${approvalRequestsCount} notification(s) non lue(s)` : 'Aucune notification non lue'}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span
                    className={`absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 rounded-full min-w-[20px] shadow-lg ${
                      approvalRequestsCount > 0 ? 'bg-orange-500 animate-pulse' : 'bg-gray-400'
                    }`}
                  >
                    {approvalRequestsCount > 99 ? '99+' : approvalRequestsCount}
                  </span>
                </Link>
              )}
              {/* User Profile Menu */}
              <div className="relative hidden md:block z-[99999]" ref={profileMenuDesktopRef}>
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className={`flex items-center gap-3 px-4 py-2 bg-white rounded-lg border ${
                    profileMenuOpen 
                      ? 'border-gray-300 shadow-md ring-2 ring-gray-100' 
                      : 'border-gray-200 hover:border-gray-300'
                  } hover:bg-gray-50 transition-all duration-200 cursor-pointer group`}
                >
                  <div className="relative">
                    <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                    {profileMenuOpen && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                    )}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors">
                      {user?.name}
                    </div>
                    <div className="text-xs text-gray-500 capitalize group-hover:text-gray-600 transition-colors">
                      {user?.role}
                    </div>
                  </div>
                  <svg 
                    className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${profileMenuOpen ? 'rotate-180 text-indigo-600' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu with animation */}
                <div
                  className={`absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-[99999] transition-all duration-200 ${
                    profileMenuOpen 
                      ? 'opacity-100 translate-y-0 visible' 
                      : 'opacity-0 -translate-y-2 invisible'
                  }`}
                >
                  {/* Header section with user info */}
                  <div className="px-4 py-3 bg-slate-50 border-b border-gray-200 rounded-t-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#2f6fd4] flex items-center justify-center text-white font-semibold text-sm">
                        {user?.name?.charAt(0) || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                      </div>
                    </div>
                  </div>

                  <div className="py-2">
                    {user?.id && (
                      <Link
                        to={monCompteHref}
                        onClick={() => setProfileMenuOpen(false)}
                        className={`flex items-center px-4 py-2.5 text-sm text-gray-700 transition-colors group/item ${
                          linkedPeopleProfileId
                            ? 'hover:bg-purple-50 hover:text-purple-800'
                            : 'hover:bg-indigo-50 hover:text-indigo-700'
                        }`}
                      >
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 transition-colors ${
                            linkedPeopleProfileId
                              ? 'bg-purple-100 group-hover/item:bg-purple-200'
                              : 'bg-indigo-100 group-hover/item:bg-indigo-200'
                          }`}
                        >
                          <svg
                            className={`w-4 h-4 ${linkedPeopleProfileId ? 'text-purple-600' : 'text-indigo-600'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">Mon compte</div>
                          <div className="text-xs text-gray-500">
                            {linkedPeopleProfileId
                              ? 'Vue profil, fiche membre & sécurité'
                              : 'Profil & compte de connexion'}
                          </div>
                        </div>
                        <svg
                          className={`w-4 h-4 text-gray-400 transition-colors ${
                            linkedPeopleProfileId ? 'group-hover/item:text-purple-600' : 'group-hover/item:text-indigo-600'
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    )}

                    {linkedPeopleProfileId && iamSecurityHref && (
                      <Link
                        to={iamSecurityHref}
                        onClick={() => setProfileMenuOpen(false)}
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors group/item"
                      >
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 group-hover/item:bg-indigo-200 flex items-center justify-center mr-3 transition-colors">
                          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">Compte IAM</div>
                          <div className="text-xs text-gray-500">Mot de passe, PIN, sessions</div>
                        </div>
                        <svg className="w-4 h-4 text-gray-400 group-hover/item:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    )}

                    {/* Paramètres */}
                    {hasPermission('settings.view') && (
                      <Link
                        to={toPortalAdminNavHref(location.pathname, '/settings')}
                        onClick={() => setProfileMenuOpen(false)}
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors group/item"
                      >
                        <div className="w-8 h-8 rounded-lg bg-blue-100 group-hover/item:bg-blue-200 flex items-center justify-center mr-3 transition-colors">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">Paramètres</div>
                          <div className="text-xs text-gray-500">Configuration du système</div>
                        </div>
                        <svg className="w-4 h-4 text-gray-400 group-hover/item:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    )}

                    {/* Statistiques & audits (même entrée que la sidebar) */}
                    {canViewAuditInsights && (
                      <Link
                        to={toPortalAdminNavHref(location.pathname, '/admin')}
                        onClick={() => setProfileMenuOpen(false)}
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors group/item"
                      >
                        <div className="w-8 h-8 rounded-lg bg-purple-100 group-hover/item:bg-purple-200 flex items-center justify-center mr-3 transition-colors">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">Statistiques & audits</div>
                          <div className="text-xs text-gray-500">Indicateurs, journaux, sessions</div>
                        </div>
                        <svg className="w-4 h-4 text-gray-400 group-hover/item:text-purple-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    )}
                  </div>

                  {/* Séparateur */}
                  {(linkedPeopleProfileId || hasPermission('settings.view') || isOrgOrSaasAdminRole(role)) && (
                    <div className="border-t border-gray-200 my-2"></div>
                  )}

                  {/* Déconnexion */}
                  <div className="px-2 pb-2">
                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors group/item"
                    >
                      <div className="w-8 h-8 rounded-lg bg-red-100 group-hover/item:bg-red-200 flex items-center justify-center mr-3 transition-colors">
                        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium">{t('layout.logout')}</div>
                        <div className="text-xs text-red-500">{t('layout.logoutHint')}</div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile User Menu */}
              <div className="md:hidden relative z-[99999]" ref={profileMenuMobileRef}>
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="relative w-11 h-11 min-w-[44px] min-h-[44px] rounded-full bg-[#2f6fd4] flex items-center justify-center text-white font-semibold text-sm hover:bg-[#2860b8] transition-colors"
                >
                  {user?.name?.charAt(0) || 'U'}
                  {profileMenuOpen && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                  )}
                </button>
                <div
                  className={`absolute right-0 top-12 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-[99999] transition-all duration-200 ${
                    profileMenuOpen 
                      ? 'opacity-100 translate-y-0 visible' 
                      : 'opacity-0 -translate-y-2 invisible'
                  }`}
                >
                  {/* Header section with user info */}
                  <div className="px-4 py-3 bg-slate-50 border-b border-gray-200 rounded-t-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#2f6fd4] flex items-center justify-center text-white font-semibold text-sm">
                        {user?.name?.charAt(0) || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                      </div>
                    </div>
                  </div>

                  <div className="py-2">
                    {user?.id && (
                      <Link
                        to={monCompteHref}
                        onClick={() => setProfileMenuOpen(false)}
                        className={`flex items-center px-4 py-2.5 text-sm text-gray-700 transition-colors group/item ${
                          linkedPeopleProfileId
                            ? 'hover:bg-purple-50 hover:text-purple-800'
                            : 'hover:bg-indigo-50 hover:text-indigo-700'
                        }`}
                      >
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 transition-colors ${
                            linkedPeopleProfileId
                              ? 'bg-purple-100 group-hover/item:bg-purple-200'
                              : 'bg-indigo-100 group-hover/item:bg-indigo-200'
                          }`}
                        >
                          <svg
                            className={`w-4 h-4 ${linkedPeopleProfileId ? 'text-purple-600' : 'text-indigo-600'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">Mon compte</div>
                          <div className="text-xs text-gray-500">
                            {linkedPeopleProfileId
                              ? 'Vue profil, fiche membre & sécurité'
                              : 'Profil & compte de connexion'}
                          </div>
                        </div>
                        <svg
                          className={`w-4 h-4 text-gray-400 transition-colors ${
                            linkedPeopleProfileId ? 'group-hover/item:text-purple-600' : 'group-hover/item:text-indigo-600'
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    )}

                    {linkedPeopleProfileId && iamSecurityHref && (
                      <Link
                        to={iamSecurityHref}
                        onClick={() => setProfileMenuOpen(false)}
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors group/item"
                      >
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 group-hover/item:bg-indigo-200 flex items-center justify-center mr-3 transition-colors">
                          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">Compte IAM</div>
                          <div className="text-xs text-gray-500">Mot de passe, PIN, sessions</div>
                        </div>
                        <svg className="w-4 h-4 text-gray-400 group-hover/item:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    )}
                    {hasPermission('settings.view') && (
                      <Link
                        to={toPortalAdminNavHref(location.pathname, '/settings')}
                        onClick={() => setProfileMenuOpen(false)}
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors group/item"
                      >
                        <div className="w-8 h-8 rounded-lg bg-blue-100 group-hover/item:bg-blue-200 flex items-center justify-center mr-3 transition-colors">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">Paramètres</div>
                          <div className="text-xs text-gray-500">Configuration du système</div>
                        </div>
                        <svg className="w-4 h-4 text-gray-400 group-hover/item:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    )}
                    {canViewAuditInsights && (
                      <Link
                        to={toPortalAdminNavHref(location.pathname, '/admin')}
                        onClick={() => setProfileMenuOpen(false)}
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors group/item"
                      >
                        <div className="w-8 h-8 rounded-lg bg-purple-100 group-hover/item:bg-purple-200 flex items-center justify-center mr-3 transition-colors">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">Statistiques & audits</div>
                          <div className="text-xs text-gray-500">Indicateurs, journaux, sessions</div>
                        </div>
                        <svg className="w-4 h-4 text-gray-400 group-hover/item:text-purple-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    )}
                  </div>

                  {(linkedPeopleProfileId || hasPermission('settings.view') || isOrgOrSaasAdminRole(role)) && (
                    <div className="border-t border-gray-200 my-2"></div>
                  )}

                  <div className="px-2 pb-2">
                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors group/item"
                    >
                      <div className="w-8 h-8 rounded-lg bg-red-100 group-hover/item:bg-red-200 flex items-center justify-center mr-3 transition-colors">
                        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium">{t('layout.logout')}</div>
                        <div className="text-xs text-red-500">{t('layout.logoutHint')}</div>
                      </div>
                    </button>
                </div>
                </div>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-[#f5f7fa] p-3 pb-[calc(5.25rem+env(safe-area-inset-bottom,0px))] sm:p-6 md:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Bottom Navigation Bar - Mobile Only - Native Style */}
      <nav className="fixed bottom-0 left-0 right-0 lg:hidden z-40 safe-area-bottom">
        {/* Backdrop blur effect (iOS style) */}
        <div className="absolute inset-0 bg-white/80 backdrop-blur-xl border-t border-gray-200/50"></div>
        <div className="relative flex items-center justify-around h-20 px-2 pb-safe">
          {/* Navigation principale - max 4 éléments */}
          {navigation.slice(0, 4).map((item) => {
            const navTo = toPortalAdminNavHref(location.pathname, item.href);
            const isActive = isPortalAdminNavActive(location.pathname, item.href, navTo);
            return (
              <Link
                key={item.id}
                to={navTo}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex flex-col items-center justify-center flex-1 h-full px-2 py-2 transition-all duration-200 active:scale-95 ${
                  isActive
                    ? 'text-[#2f6fd4]'
                    : 'text-gray-600'
                }`}
              >
                <div className={`relative mb-1.5 ${isActive ? 'scale-110' : ''} transition-transform duration-200`}>
                  <svg
                    className={`h-7 w-7 ${isActive ? 'text-[#2f6fd4]' : 'text-gray-500'}`}
                    fill={isActive ? 'currentColor' : 'none'}
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={isActive ? 0 : 2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                  </svg>
                  {isActive && (
                    <div className="absolute -bottom-1 left-1/2 h-0.5 w-4 -translate-x-1/2 rounded-full bg-[#2f6fd4]"></div>
                  )}
                </div>
                <span className={`text-[11px] font-semibold ${isActive ? 'text-[#2f6fd4]' : 'text-gray-600'}`}>
                  {t(`navShort.${item.id}`)}
                </span>
              </Link>
            );
          })}
          
          {/* Bouton Menu (dernière position) */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`flex flex-col items-center justify-center flex-1 h-full px-2 py-2 transition-all duration-200 active:scale-95 ${
              mobileMenuOpen
                ? 'text-[#2f6fd4]'
                : 'text-gray-600'
            }`}
          >
            <div className={`relative mb-1.5 ${mobileMenuOpen ? 'scale-110' : ''} transition-transform duration-200`}>
              <svg
                className={`h-7 w-7 ${mobileMenuOpen ? 'text-[#2f6fd4]' : 'text-gray-500'}`}
                fill={mobileMenuOpen ? 'currentColor' : 'none'}
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={mobileMenuOpen ? 0 : 2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              {mobileMenuOpen && (
                <div className="absolute -bottom-1 left-1/2 h-0.5 w-4 -translate-x-1/2 rounded-full bg-[#2f6fd4]"></div>
              )}
            </div>
            <span className={`text-[11px] font-semibold ${mobileMenuOpen ? 'text-[#2f6fd4]' : 'text-gray-600'}`}>
              {t('layout.menu')}
            </span>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Panel - Native Style with Slide Animation */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop with fade animation */}
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 lg:hidden animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel with slide-up animation */}
          <div 
            className="fixed bottom-0 left-0 right-0 z-50 lg:hidden animate-in slide-in-from-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Backdrop blur effect */}
            <div className="absolute inset-0 bg-white/95 backdrop-blur-2xl rounded-t-3xl shadow-2xl"></div>
            
            <div className="relative max-h-[80vh] overflow-y-auto rounded-t-3xl">
              {/* Drag Handle Indicator (iOS style) */}
              <div className="sticky top-0 z-10 pt-3 pb-2 flex justify-center bg-white/95 backdrop-blur-xl rounded-t-3xl">
                <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
              </div>
              
              <div className="px-4 pb-6 pt-2">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 py-3">
                  <h3 className="text-xl font-bold text-gray-900">{t('layout.menu')}</h3>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:scale-95 transition-all"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Navigation principale restante */}
                {navigation.slice(4).length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-xs font-semibold text-gray-400 uppercase mb-3 px-3 tracking-wider">{t('layout.navigation')}</h4>
                    <div className="space-y-1 bg-gray-50/50 rounded-2xl p-2">
                      {navigation.slice(4).map((item) => {
                        const navTo = toPortalAdminNavHref(location.pathname, item.href);
                        const isActive = isPortalAdminNavActive(location.pathname, item.href, navTo);
                        return (
                          <Link
                            key={item.id}
                            to={navTo}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`relative flex items-center gap-4 px-4 py-3.5 rounded-xl transition-colors active:scale-[0.98] ${
                              isActive
                                ? 'bg-[#2f6fd4]/10 text-[#1F4668]'
                                : 'text-gray-700 bg-white hover:bg-gray-100 active:bg-gray-200'
                            }`}
                          >
                            {isActive ? (
                              <span
                                aria-hidden
                                className="absolute bottom-2 left-0 top-2 w-[3px] rounded-full bg-[#2f6fd4]"
                              />
                            ) : null}
                            <div className={`p-2 rounded-lg ${isActive ? 'bg-[#2f6fd4]/15' : 'bg-gray-100'}`}>
                              <svg
                                className={`h-5 w-5 ${isActive ? 'text-[#2f6fd4]' : 'text-gray-600'}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                              </svg>
                            </div>
                            <span className="font-semibold flex-1">{t(item.nameKey)}</span>
                            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Navigation Admin */}
                {adminNavigation.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-xs font-semibold text-gray-400 uppercase mb-3 px-3 tracking-wider">{t('layout.administration')}</h4>
                    <div className="space-y-2 bg-gray-50/50 rounded-2xl p-2">
                      {adminNavSections.map((section) => (
                        <div key={`mobile-${section.key}`} className="space-y-1">
                          <p className="px-2 pt-1 text-[10px] uppercase tracking-wider text-gray-500">
                            {t(section.titleKey)}
                          </p>
                          {section.items.map((item) => {
                            const navTo = toPortalAdminNavHref(location.pathname, item.href);
                            const isActive = isPortalAdminNavActive(location.pathname, item.href, navTo);
                            return (
                              <Link
                                key={item.id}
                                to={navTo}
                                onClick={() => setMobileMenuOpen(false)}
                                className={`relative flex items-center gap-4 px-4 py-3.5 rounded-xl transition-colors active:scale-[0.98] ${
                                  isActive
                                    ? 'bg-[#2f6fd4]/10 text-[#1F4668]'
                                    : 'text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100'
                                }`}
                              >
                                {isActive ? (
                                  <span
                                    aria-hidden
                                    className="absolute bottom-2 left-0 top-2 w-[3px] rounded-full bg-[#2f6fd4]"
                                  />
                                ) : null}
                                <div className={`p-2 rounded-lg ${isActive ? 'bg-[#2f6fd4]/15' : 'bg-gray-100'}`}>
                                  <svg
                                    className={`h-5 w-5 ${isActive ? 'text-[#2f6fd4]' : 'text-gray-600'}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                                  </svg>
                                </div>
                                <span className="font-semibold flex-1">{t(item.nameKey)}</span>
                                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                              </Link>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notifications non lues si applicable */}
                {canApprove && approvalRequestsCount > 0 && (
                  <div className="mb-6">
                    <Link
                      to="/notifications?tab=approvals"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-4 px-4 py-4 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg active:scale-[0.98] transition-all"
                    >
                      <div className="p-2.5 bg-white/20 rounded-xl">
                        <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="font-bold flex-1 text-lg">Notifications</span>
                      <div className="px-3 py-1.5 bg-white/30 rounded-full">
                        <span className="text-sm font-bold">{approvalRequestsCount}</span>
                      </div>
                    </Link>
                  </div>
                )}

                {/* Notifications */}
                <div className="mb-4 pt-4 border-t border-gray-200/50">
                  <Link
                    to="/notifications"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-gray-700 bg-white hover:bg-gray-50 active:scale-[0.98] transition-all"
                  >
                    <div className="relative p-2 bg-gray-100 rounded-xl">
                      <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <span
                        className={`absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9px] font-bold leading-none text-white rounded-full ${
                          unreadAnnouncementsCount > 0 ? 'bg-red-500' : 'bg-gray-400'
                        }`}
                      >
                        {unreadAnnouncementsCount > 99 ? '99+' : unreadAnnouncementsCount}
                      </span>
                    </div>
                    <span className="font-semibold flex-1">Notifications</span>
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
    </>
  );
};

export default DashboardLayout;